import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Form, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Label } from '@/components/ui/label';
import { AppDataKeyField } from '@/form/AppDataKeyField';
import { AppInputField, AppPromptsField } from '@/form/FormUI';
import type { NodeFormProps, RoomStarterSelectionNodeType } from '@/nodes';
import { useSavedFormValues } from '@/state';
import { extractFieldErrors } from '@/util/extractFieldErrors';
import { zodResolver } from '@hookform/resolvers/zod';
import {
    RoomStarterSelectionDataSchema,
    type RoomStarterSelectionDataType,
    type RoomType,
    RoomTypeLabels,
} from '@rock/types';
import { type FC, useCallback, useEffect } from 'react';
import { type FieldErrors, useForm, useWatch } from 'react-hook-form';
import { toast } from 'sonner';
import type { z } from 'zod';

type FormInput = z.input<typeof RoomStarterSelectionDataSchema>;

export const RoomStarterSelectionForm: FC<NodeFormProps<RoomStarterSelectionNodeType>> = ({
    flowId,
    node,
    updateNode,
    cancelUpdate,
}) => {
    const [formState, setFormState] = useSavedFormValues<FormInput>({ flowId, nodeId: node.id });
    const form = useForm<FormInput, unknown, RoomStarterSelectionDataType>({
        resolver: zodResolver(RoomStarterSelectionDataSchema),
        defaultValues: formState || node.data,
    });

    const watchedFields = useWatch({ control: form.control });
    useEffect(() => {
        setFormState(watchedFields);
    }, [setFormState, watchedFields]);

    const onSubmit = useCallback(
        (values: RoomStarterSelectionDataType) => {
            setFormState(null);
            updateNode({
                ...node,
                data: values,
            });
        },
        [setFormState, node, updateNode],
    );

    const onInvalid = useCallback((errors: FieldErrors) => {
        toast.error('Validation Errors', {
            description: extractFieldErrors(errors).join(', '),
        });
    }, []);

    const onCancel = useCallback(() => {
        setFormState(null);
        cancelUpdate();
    }, [setFormState, cancelUpdate]);

    return (
        <Form {...form}>
            <form
                onSubmit={form.handleSubmit(onSubmit, onInvalid)}
                className="flex flex-1 flex-col gap-8 items-stretch overflow-hidden"
            >
                <div className="flex flex-1 flex-col gap-8 items-stretch p-5 overflow-scroll">
                    <AppInputField
                        label="Page Title"
                        placeholder="Page Title"
                        control={form.control}
                        name="pageTitle"
                        description="The title displayed in the navigation header."
                    />

                    <AppDataKeyField
                        control={form.control}
                        name="dataKey"
                        label="Data Key"
                        placeholder="selected_rooms"
                        description="Where the selected room ids are stored in the guide values."
                        mode="produce"
                        currentNodeId={node.id}
                    />

                    <FormField
                        control={form.control}
                        name="preselectRoomTypes"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Pre-Checked Room Types</FormLabel>
                                <FormDescription>
                                    Existing rooms of these types start CHECKED — the node itself knows nothing about
                                    any space; author the expectation per guide (e.g. Kitchen + Laundry for appliances).
                                </FormDescription>
                                <div className="grid grid-cols-2 gap-2">
                                    {(Object.entries(RoomTypeLabels) as [RoomType, string][]).map(([value, label]) => {
                                        const selectedTypes: RoomType[] = field.value ?? [];
                                        const checked = selectedTypes.includes(value);
                                        return (
                                            <div key={value} className="flex flex-row items-center gap-2">
                                                <Checkbox
                                                    id={`preselect-${value}`}
                                                    className="bg-white"
                                                    checked={checked}
                                                    onCheckedChange={(next) =>
                                                        field.onChange(
                                                            next
                                                                ? [...selectedTypes, value]
                                                                : selectedTypes.filter((t) => t !== value),
                                                        )
                                                    }
                                                />
                                                <Label htmlFor={`preselect-${value}`} className="text-sm font-normal">
                                                    {label}
                                                </Label>
                                            </div>
                                        );
                                    })}
                                </div>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    <AppInputField
                        label="Done Label"
                        placeholder="e.g. These Are My Rooms"
                        control={form.control}
                        name="doneLabel"
                        description="The button that commits the selection and moves on."
                    />

                    <AppPromptsField description="What text to show above the room selection?" />
                </div>

                <div className="flex flex-row gap-3 p-5">
                    <Button type="submit" className="flex-1">
                        Save Details
                    </Button>
                    <Button type="button" className="flex-1" variant="outline" onClick={onCancel}>
                        Cancel
                    </Button>
                </div>
            </form>
        </Form>
    );
};
